import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calendar,
  Briefcase,
  CheckCircle2,
  ArrowRight,
  Quote,
} from "lucide-react";
import { CONTACT } from "./constants";

export default function PortfolioDialog({ project, open, onOpenChange }) {
  if (!project) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="portfolio-dialog"
        className="!max-w-4xl !w-[95vw] !p-0 !gap-0 !rounded-3xl overflow-hidden max-h-[92vh] flex flex-col border-slate-200 bg-white"
      >
        <DialogTitle className="sr-only">{project.title}</DialogTitle>
        <div className="relative shrink-0">
          <div className="aspect-[21/9] overflow-hidden bg-slate-100">
            <img
              src={project.img}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
            <span className="inline-flex items-center gap-1.5 bg-[var(--ab-blue)] text-white px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-widest">
              {project.category}
            </span>
            <h2 className="font-display mt-3 text-2xl sm:text-4xl font-extrabold leading-tight">
              {project.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-blue-100">
              <span className="flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5" />
                {project.client}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {project.duration} · {project.year}
              </span>
            </div>
          </div>
        </div>

        <div
          className="overflow-y-auto px-6 sm:px-10 py-8 space-y-7"
          data-testid="portfolio-dialog-body"
        >
          {/* Headline metric */}
          <div className="flex items-end gap-4 border-b border-slate-100 pb-6">
            <div className="font-display text-5xl sm:text-6xl font-extrabold ab-text-gradient leading-none">
              {project.metric}
            </div>
            <div className="text-slate-600 pb-1 text-sm sm:text-base">
              {project.metricLabel}
            </div>
          </div>

          {/* Summary */}
          <p className="text-lg text-slate-700 leading-relaxed font-medium">
            {project.summary}
          </p>

          {/* Challenge */}
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-bold text-[var(--ab-navy)]">
              Karşılaştığımız Zorluk
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {project.challenge}
            </p>
          </div>

          {/* Solution */}
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-bold text-[var(--ab-navy)]">
              Uyguladığımız Çözüm
            </h3>
            <ul className="mt-4 space-y-2.5">
              {project.solution.map((s, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-slate-700 text-[0.98rem] leading-relaxed"
                >
                  <CheckCircle2 className="h-5 w-5 text-[var(--ab-blue)] shrink-0 mt-0.5" />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          {/* Results */}
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-bold text-[var(--ab-navy)]">
              Elde Edilen Sonuçlar
            </h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {project.results.map((r) => (
                <div
                  key={r.k}
                  className="rounded-2xl border border-slate-200 bg-[var(--ab-bg-soft)] p-5"
                >
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    {r.k}
                  </div>
                  <div className="font-display text-2xl sm:text-3xl font-extrabold text-[var(--ab-navy)] mt-1">
                    {r.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="relative rounded-2xl bg-[var(--ab-navy)] text-white p-6 sm:p-8 overflow-hidden">
            <Quote className="absolute top-5 right-6 h-10 w-10 text-white/10" />
            <p className="font-display text-lg sm:text-xl leading-relaxed">
              &ldquo;{project.quote}&rdquo;
            </p>
            <div className="mt-4 text-sm text-blue-200 font-semibold">
              — {project.quoteBy}
            </div>
          </div>

          {/* CTA */}
          <div className="p-6 rounded-2xl bg-[var(--ab-bg-soft)] border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="font-display font-bold text-[var(--ab-navy)]">
                Benzer bir sonucu sizin için de yapalım mı?
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Projenizi WhatsApp&apos;tan kısaca anlatın, ücretsiz analiz hazırlayalım.
              </div>
            </div>
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portfolio-dialog-cta"
              className="ab-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold shrink-0"
            >
              Projeni Konuşalım
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
